import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { ChevronDown } from 'lucide-react';
import { ReactNode, RefObject, useCallback, useEffect, useState } from 'react';
import { Checkbox } from '@components/ui/checkbox';
import { Option } from './types';
import { cn } from '@utils/cn';
import { useTranslation } from 'react-i18next';
import { FieldError } from 'react-hook-form';
import { hasUnknownOption } from '@forms/helpers';

export interface MultiSelectProps {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  className?: string;
  placeholder?: string;
  error?: FieldError;
  disabled?: boolean;
  limit?: number;
  singleMode?: boolean;
  defaultOpen?: boolean;
  open?: boolean;
  popoverRef?: RefObject<HTMLElement | null>;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  onCreate?: (value: string) => void;
  onOpenChange?: (open: boolean) => void;
  matchTriggerWidth?: boolean;
  renderTrigger?: (open: boolean) => ReactNode;
  contentClassName?: string;
}

const VISIBLE_FILTERS_LIMIT = 2;

export const MultiSelect = ({
  options,
  value,
  onChange,
  className,
  placeholder,
  error,
  disabled,
  limit = VISIBLE_FILTERS_LIMIT,
  singleMode,
  defaultOpen,
  open: controlledOpen,
  popoverRef,
  align = 'start',
  side = 'bottom',
  onCreate,
  onOpenChange,
  matchTriggerWidth,
  renderTrigger,
  contentClassName,
}: MultiSelectProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'forms.default.multiSelect' });
  const [internalOpen, setOpen] = useState(!!defaultOpen);
  const open = controlledOpen ?? internalOpen;
  const [searchValue, setSearchValue] = useState('');

  const optionsMap = Object.fromEntries(
    options.map((el) => [el.value?.toLocaleLowerCase(), el.label?.toLocaleLowerCase()]),
  );

  const isOptionChecked = useCallback((v: string) => value.includes(v), [value]);

  const lockedValues = new Set(options.filter((el) => el.disabled).map((el) => el.value));

  const selectAllValues = options
    .filter((el) => !el.disabled || value.includes(el.value))
    .map((el) => el.value);

  const clearValues = value.filter((v) => lockedValues.has(v));

  const setOptionChecked = (v: string) => {
    if (lockedValues.has(v)) return;
    if (isOptionChecked(v)) onChange(value.filter((el) => el !== v));
    else onChange(singleMode ? [v] : [...value, v]);
    if (singleMode) handleOpenChange(false);
  };

  const generateChecked = () => {
    if (!Array.isArray(value)) throw new Error('Value is not an array!');
    const items = [];
    for (const [index, item] of value.entries()) {
      if (index < limit) items.push(options.find((el) => el.value === item)?.label || item);
      else {
        items.push(`+${value.length - limit}`);
        break;
      }
    }
    return items.join(', ');
  };

  const hasInvalidOption = value.some((v) =>
    hasUnknownOption(
      options.map((el) => ({ name: el.label, value: el.value })),
      v,
    ),
  );

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  useEffect(() => {
    if (defaultOpen) onOpenChange?.(true);
  }, []);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild disabled={disabled}>
        {renderTrigger ? (
          renderTrigger(open)
        ) : (
          <button
            className={cn(
              'group-[.error]:shadow-danger-b flex h-8 w-full items-center justify-between gap-2 rounded border border-neutral-300 bg-white px-3 text-sm font-normal text-neutral-800 focus-within:border-primary-600 focus-within:outline focus-within:outline-2 focus-within:outline-primary-200',
              (error || hasInvalidOption) && 'border-danger-500 focus-within:border-danger-500',
              disabled && 'border-neutral-200 bg-neutral-50 text-neutral-400',
              className,
            )}>
            {value.length ? (
              <span
                className={cn(matchTriggerWidth && 'min-w-0 truncate')}
                title={matchTriggerWidth ? generateChecked() : undefined}>
                {generateChecked()}
              </span>
            ) : (
              <span className="text-sm font-normal">{placeholder || t('select')}</span>
            )}
            <ChevronDown className="text-neutral-90 h-4 w-4 shrink-0" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align={align}
        side={side}
        container={popoverRef?.current}
        className={cn(
          'p-0',
          !singleMode && 'w-[200px]',
          matchTriggerWidth &&
            'w-[var(--radix-popover-trigger-width)] max-w-[var(--radix-popover-trigger-width)]',
          contentClassName,
        )}>
        <Command
          filter={(v, search) =>
            Number(optionsMap[v]?.toLocaleLowerCase()?.includes(search?.toLocaleLowerCase()))
          }>
          <CommandInput
            value={searchValue}
            onValueChange={setSearchValue}
            placeholder={t('search')}
          />
          <CommandList>
            <CommandEmpty>{t('empty')}</CommandEmpty>
            <CommandGroup className="scrollbar-thin max-h-[238px] overflow-y-auto p-0">
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  className={cn([
                    'flex gap-2',
                    option.sublabel && 'justify-between',
                    isOptionChecked(option.value)
                      ? 'bg-primary-500 text-white '
                      : 'hover:bg-primary-200',
                    option.disabled && 'cursor-not-allowed opacity-60',
                  ])}
                  title={option.disabled ? option.disabledReason : undefined}
                  value={option.value}
                  disabled={disabled || option.disabled}
                  onSelect={() => {
                    setOptionChecked(option.value);
                  }}>
                  {!singleMode && <Checkbox checked={isOptionChecked(option.value)} />}
                  {matchTriggerWidth ? (
                    <span className="min-w-0 flex-1 truncate" title={option.label}>
                      {option.label}
                    </span>
                  ) : (
                    option.label
                  )}
                  {option.sublabel && (
                    <span className="text-xs text-neutral-300">{option.sublabel}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
            {onCreate && searchValue && (
              <div className="mr-2.5 flex justify-between border-t">
                <span className="px-2 py-1.5 text-xs">{searchValue}</span>
                <button
                  type="button"
                  disabled={!searchValue.length}
                  className="cursor-pointer text-xs font-medium text-primary-600 hover:underline disabled:cursor-default disabled:no-underline disabled:opacity-50"
                  onClick={() => {
                    onCreate(searchValue);
                    setSearchValue('');
                  }}>
                  {t('createNew').toLocaleUpperCase()}
                </button>
              </div>
            )}
            <div className="grid grid-cols-[repeat(2,1fr)] border-t px-2.5 py-1.5">
              {!singleMode && (
                <button
                  type="button"
                  disabled={value.length === selectAllValues.length}
                  className="col-start-1 w-fit cursor-pointer text-xs font-medium text-primary-600 hover:underline disabled:cursor-default disabled:no-underline disabled:opacity-50"
                  onClick={() => onChange(selectAllValues)}>
                  {t('selectAll').toLocaleUpperCase()}
                </button>
              )}
              <button
                type="button"
                disabled={clearValues.length === value.length}
                className="col-start-2 w-fit cursor-pointer justify-self-end text-right text-xs font-medium text-primary-600 hover:underline disabled:cursor-default disabled:no-underline disabled:opacity-50"
                onClick={() => onChange(clearValues)}>
                {t('clear').toLocaleUpperCase()}
              </button>
            </div>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
